import React from 'react';
import PropTypes from 'prop-types';

import CustomRadioButton from '../custom_radio_button';
import RequiredReasonTextarea from '../required_reason_textarea';
import { INITIATOR_OPTIONS, SCREENSHOT_TYPES } from './care_request_rules';

// PROD-7828 E1: the "who asked, and when" fields every Care change flow shows. Props-only; the host owns the draft.
// A host that already has the initiator (cancel radios) or a mandatory note (apis reason) hides those here.
export function CareRequestFields(props) {
  const {
    draft,
    errors,
    onChange,
    showInitiatedBy,
    initiatedByFixed,
    showNote,
    requestedLabel,
    changeMadeAtText
  } = props;
  return (
    <div className="care-request-fields" style={{ margin: '10px 0' }}>
      {showInitiatedBy ? (
        <div style={{ marginBottom: '8px' }}>
          <div style={{ fontWeight: 600, marginBottom: '6px' }}>Request initiated by</div>
          {initiatedByFixed ? (
            <div data-testid="care-initiated-fixed">
              {INITIATOR_OPTIONS.find((o) => o.value === initiatedByFixed)?.label}
            </div>
          ) : (
            <div role="radiogroup" aria-label="Request initiated by">
              {INITIATOR_OPTIONS.map((o) => (
                <div key={o.value} style={{ marginBottom: '4px' }}>
                  <CustomRadioButton
                    label={o.label}
                    selected={draft.initiated_by === o.value}
                    onRadioClick={() => onChange({ initiated_by: o.value })}
                  />
                </div>
              ))}
            </div>
          )}
          {errors.initiated_by ? (
            <p style={{ color: 'var(--state-error)' }}>* {errors.initiated_by}</p>
          ) : null}
        </div>
      ) : null}
      <label style={{ display: 'block', marginBottom: '8px' }}>
        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{requestedLabel}</div>
        <input
          type="datetime-local"
          aria-label={requestedLabel}
          value={draft.requested_at || ''}
          onChange={(e) => onChange({ requested_at: e.target.value })}
          style={{ width: '300px', maxWidth: '100%' }}
        />
      </label>
      {errors.requested_at ? (
        <p style={{ color: 'var(--state-error)' }}>* {errors.requested_at}</p>
      ) : null}
      {changeMadeAtText ? (
        <div style={{ marginBottom: '8px', color: 'var(--text-secondary)' }}>
          Change made at: {changeMadeAtText}
        </div>
      ) : null}
      {showNote ? (
        <RequiredReasonTextarea
          label="Note: how and what they asked (required)"
          value={draft.note || ''}
          onChange={(e) => onChange({ note: e.target.value })}
          placeholder="Note: how and what they asked (required)"
          error={!!errors.note}
          errorText={errors.note}
          height="60px"
        />
      ) : null}
      <label style={{ display: 'block', marginBottom: '8px' }}>
        <div style={{ marginBottom: '4px' }}>Screenshot (optional)</div>
        <input
          type="file"
          aria-label="Screenshot (optional)"
          accept={SCREENSHOT_TYPES.join(',')}
          onChange={(e) =>
            onChange({ file: e.target.files && e.target.files[0] ? e.target.files[0] : null })
          }
        />
      </label>
      {errors.file ? <p style={{ color: 'var(--state-error)' }}>* {errors.file}</p> : null}
    </div>
  );
}

CareRequestFields.propTypes = {
  draft: PropTypes.shape({
    initiated_by: PropTypes.string,
    requested_at: PropTypes.string,
    note: PropTypes.string
  }).isRequired,
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  showInitiatedBy: PropTypes.bool,
  initiatedByFixed: PropTypes.string,
  showNote: PropTypes.bool,
  requestedLabel: PropTypes.string,
  changeMadeAtText: PropTypes.string
};

CareRequestFields.defaultProps = {
  errors: {},
  showInitiatedBy: true,
  initiatedByFixed: '',
  showNote: false,
  requestedLabel: 'Requested at (IST)',
  changeMadeAtText: 'recorded by the system when you save'
};

// Shown when the booking change saved but the request details did not: Retry resends only the details (same uuid).
export function CareSaveRetry(props) {
  const { message, busy, onRetry, onClose } = props;
  return (
    <div role="alert" className="care-save-retry" style={{ margin: '10px 0' }}>
      <p style={{ color: 'var(--state-error)' }}>
        The session change was saved, but the request details were not: {message}
      </p>
      <button type="button" disabled={busy} onClick={onRetry}>
        {busy ? 'Saving…' : 'Retry saving details'}
      </button>{' '}
      <button type="button" disabled={busy} onClick={onClose}>
        Close (add details later from the session page)
      </button>
    </div>
  );
}

CareSaveRetry.propTypes = {
  message: PropTypes.string.isRequired,
  busy: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

CareSaveRetry.defaultProps = { busy: false };
